// axiosCurlLogger.ts
import { AxiosError, AxiosRequestConfig } from 'axios';

interface EndpointDescription {
  path: string;
  method: string;
  description: string;
}

const styleConstants = {
  heading: [
    'background: #2563eb',
    'color: white',
    'padding: 4px 8px',
    'border-radius: 4px',
    'font-weight: bold',
  ].join(';'),

  errorHeading: [
    'background: #dc2626',
    'color: white',
    'padding: 4px 8px',
    'border-radius: 4px',
    'font-weight: bold',
  ].join(';'),

  borderTop: ['color: #64748b', 'font-weight: bold'].join(';'),

  borderBottom: ['color: #64748b', 'font-weight: bold'].join(';'),

  apiDescription: [
    'color: #0ea5e9',
    'font-size: 14px',
    'font-weight: bold',
    'font-family: monospace',
  ].join(';'),

  apiPath: [
    'background: #1e293b',
    'color: #38bdf8',
    'padding: 2px 6px',
    'border-radius: 3px',
    'font-family: monospace',
  ].join(';'),

  curl: ['color: #6366f1', 'font-weight: bold'].join(';'),

  method: ['color: #2563eb', 'font-weight: bold'].join(';'),

  url: ['color: #059669', 'font-weight: bold'].join(';'),

  params: ['color: #7c3aed'].join(';'),

  headerFlag: ['color: #ea580c', 'font-weight: bold'].join(';'),

  headerName: ['color: #f0bb78'].join(';'),

  headerValue: ['color: #2563eb'].join(';'),

  dataFlag: ['color: #db2777', 'font-weight: bold'].join(';'),

  dataContent: ['color: #9333ea'].join(';'),
};

const borders = {
  top: '='.repeat(40),
  bottom: '='.repeat(40),
};

const pathMatches = (templatePath: string, actualPath: string): boolean => {
  const templateParts = templatePath.split('/');
  const actualParts = actualPath.split('/');

  if (templateParts.length !== actualParts.length) {
    return false;
  }

  return templateParts.every((templatePart, index) => {
    // If template part is a parameter (enclosed in {}), it matches anything
    if (templatePart.startsWith('{') && templatePart.endsWith('}')) {
      return true;
    }
    return templatePart === actualParts[index];
  });
};

const getApiDescription = (config: AxiosRequestConfig): string => {
  const method = config.method?.toUpperCase() || 'GET';
  const url = config.url || '';

  // Extract the endpoint path - remove query params if present
  const endpoint = url.split('?')[0];

  // Clean up the endpoint - remove leading/trailing slashes
  const cleanEndpoint = endpoint.replace(/^\/+|\/+$/g, '');

  // Try to find a matching description
  const matchingDescription = endpointDescriptions.find(
    desc => desc.method === method && pathMatches(desc.path, cleanEndpoint),
  );

  if (matchingDescription) {
    return `${method} → ${matchingDescription.description}`;
  }
  // Fallback to the original behavior if no match is found
  const pathSegments = cleanEndpoint.split('/');
  const fallbackDescription = pathSegments
    .map(segment => {
      return segment
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    })
    .join(' → ');

  return `${method} → ${fallbackDescription}`;
};

const formatHeader = (
  key: string,
  value: string,
): { text: string; styles: string[] } => {
  let apiKey = 'YOUR_APIKEY_FROM_SETTINGS';
  const permission = JSON.parse(localStorage.getItem('rolePermission') || '');
  const localApi = localStorage.getItem('ApiKey');
  if (localApi && localApi !== '' && permission.viewApiKey) apiKey = localApi;
  const updatedValue = key === 'Authorization' ? `Bearer ${apiKey}` : value;
  return {
    text: `%c-H%c '${key}%c: ${updatedValue}%c'`,
    styles: [
      styleConstants.headerFlag,
      styleConstants.headerName,
      styleConstants.headerValue,
      '',
    ],
  };
};

const generateCurl = (
  config: AxiosRequestConfig,
): { text: string; styles: string[] } => {
  const method = config.method?.toUpperCase() || 'GET';
  const urlPath = config.url?.startsWith('/') ? config.url : `/${config.url}`;
  const url = config.baseURL ? `${config.baseURL}${urlPath}` : urlPath;

  const parts: string[] = [];
  const styleList: string[] = [];

  // Start with curl command
  parts.push('%ccurl%c');
  styleList.push(styleConstants.curl, '');

  // Add method
  parts.push('%c-X%c %c' + method + '%c');
  styleList.push(styleConstants.headerFlag, '', styleConstants.method, '');

  // Add URL with potential query params
  let urlPart = `'${url}`;
  if (config.params) {
    const queryString = new URLSearchParams(config.params).toString();
    urlPart += `%c?${queryString}%c`;
    parts.push(urlPart + "'");
    styleList.push(styleConstants.url, styleConstants.params, '');
  } else {
    parts.push('%c' + urlPart + "'%c");
    styleList.push(styleConstants.url, '');
  }

  // Add headers with Content-Type enforcement
  if (config.headers || config.data) {
    const headers = { ...config.headers } as Record<string, string>;

    // Ensure Content-Type is set for requests with data
    if (config.data && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const skipHeaders = [
      'common',
      'delete',
      'get',
      'post',
      'put',
      'patch',
      'user-agent',
    ];

    Object.entries(headers)
      .filter(([key]) => !skipHeaders.includes(key.toLowerCase()))
      .forEach(([key, value]) => {
        // Skip undefined values
        if (value !== undefined) {
          const { text, styles } = formatHeader(key, value);
          parts.push(text);
          styleList.push(...styles);
        }
      });
  }

  // Add request body with special handling for messages send API
  if (config.data) {
    let data = typeof config.data === 'string' ? config.data : config.data;

    // Special handling for messages send API
    if (
      typeof data === 'object' &&
      data.messages &&
      data.messages.length > 0 &&
      config.url?.includes('messages/send')
    ) {
      // Create a deep copy to avoid modifying original data
      const modifiedData = JSON.parse(JSON.stringify(data));

      modifiedData.messages = modifiedData.messages.map(
        (message: Record<string, unknown>) => {
          // Remove refId
          const { refId: _refId, ...rest } = message;
          return rest;
        },
      );

      data = JSON.stringify(modifiedData);
    } else if (typeof data !== 'string') {
      data = JSON.stringify(data);
    }

    parts.push(`%c-d%c %c'${data}'%c`);
    styleList.push(styleConstants.dataFlag, '', styleConstants.dataContent, '');
  }

  return {
    text: parts.join(' '),
    styles: styleList,
  };
};

const isDeveloperMode = (): boolean => {
  try {
    return localStorage.getItem('DeveloperMode') === 'true';
  } catch {
    return false;
  }
};

export const curlLogger = {
  request: <T extends AxiosRequestConfig>(config: T): T => {
    if (isDeveloperMode() && !config.headers?.['skip-curl-log']) {
      const { text, styles } = generateCurl(config);
      const apiDesc = getApiDescription(config);

      console.log('\n%c' + borders.top, styleConstants.borderTop);
      console.log(
        '%c📡 API Details: %c' + apiDesc,
        styleConstants.apiDescription,
        styleConstants.apiPath,
      );
      console.log('%ccURL For API Call', styleConstants.heading);
      console.log(text, ...styles);
      console.log('%c' + borders.bottom + '\n', styleConstants.borderBottom);
    }
    return config;
  },

  error: (error: AxiosError) => {
    if (
      isDeveloperMode() &&
      error.config &&
      !error.config.headers?.['skip-curl-log']
    ) {
      const { text, styles } = generateCurl(error.config);
      const apiDesc = getApiDescription(error.config);

      console.log('\n%c' + borders.top, styleConstants.borderTop);
      console.log(
        '%c📡 API Details: %c' + apiDesc,
        styleConstants.apiDescription,
        styleConstants.apiPath,
      );
      console.log('%ccURL For Failed API Call', styleConstants.errorHeading);
      console.log(text, ...styles);
      console.log('%c' + borders.bottom + '\n', styleConstants.borderBottom);
    }
    return Promise.reject(error);
  },
};

const endpointDescriptions: EndpointDescription[] = [
  //get
  {
    path: 'flows',
    method: 'GET',
    description: 'Get all flows from this curl',
  },
  {
    path: 'templates',
    method: 'GET',
    description: 'Get all templates from this curl',
  },
  {
    path: 'role-permission',
    method: 'GET',
    description: 'Get role permissions data from this curl',
  },
  {
    path: 'business',
    method: 'GET',
    description: 'Get business details',
  },
  {
    path: 'templates/media-link',
    method: 'GET',
    description: 'Get template media link',
  },
  {
    path: 'clients',
    method: 'GET',
    description: 'Get clients from this curl',
  },
  {
    path: 'app-store/apps',
    method: 'GET',
    description: 'Get apps details',
  },
  {
    path: 'auth/business-employees',
    method: 'GET',
    description: 'Get employee details',
  },
  {
    path: 'business/profile',
    method: 'GET',
    description: 'Get profile information',
  },
  {
    path: 'business/commerce',
    method: 'GET',
    description: 'Get commerce details',
  },
  {
    path: 'campaigns',
    method: 'GET',
    description: 'Get campaign details',
  },
  //post
  {
    path: 'clients',
    method: 'POST',
    description: 'Update client details or create new client',
  },
  {
    path: 'messages/send',
    method: 'POST',
    description:
      'Send messages curl. If you send a template, then passing template content is optional. You can remove the template content. Template content is the body of the template. ',
  },
  {
    path: 'clients/chat/assign',
    method: 'POST',
    description: 'Assign or unasssign a chat to employee',
  },
  {
    path: 'business/attributes',
    method: 'POST',
    description: 'Create a attribute',
  },
  {
    path: 'campaigns/send',
    method: 'POST',
    description: 'Send a campaign',
  },
  //put
  {
    path: 'clients/chat/toggle/{phoneNumber}',
    method: 'PUT',
    description: 'Toggle chat status (closed/open) for a specific client',
  },
  {
    path: 'business/attributes',
    method: 'PUT',
    description: 'Update a attribute',
  },
  //delete
  {
    path: 'business/attributes',
    method: 'DELETE',
    description: 'Delete a attribute',
  },
];
